/**
 * Leads Database Hook
 * 
 * Provides database operations for the leads table.
 * Abstracts Supabase queries and transforms data to/from LifecycleLead format.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { LifecycleLead, LeadStatus } from '@/lib/leadStateMachine';

// --- Database Row Type ---

interface LeadRow {
  id: string;
  lead_type_id: string;
  lead_type_name: string;
  vertical: string;
  state: string;
  estimated_value: number;
  status: string;
  retry_count: number;
  max_retries: number;
  next_retry_at: number | null;
  original_price: number | null;
  current_price: number | null;
  pricing_snapshot: {
    adminMinPrice?: number;
    bidWeight: number;
    qualityWeight: number;
  } | null;
  lead_payload: Record<string, unknown> | null;
  lead_profile: Record<string, unknown> | null;
  routing_context: {
    devOverridesEnabled: boolean;
    forceAllAgentsEligible?: boolean;
    forceWalletBalance?: number;
    forceLicenses?: string[];
    // Per-agent overrides (for deterministic retries)
    perAgentOverrides?: {
      enabled: boolean;
      agentOverrides: Record<string, {
        forceAcceptAllLeadTypes: boolean;
        overrideAcceptedLeadTypes: string[];
        overrideLicensedStates: string[];
        ignoreContractCaps: boolean;
        ignorePricingRules: boolean;
      }>;
      capturedAt: number;
    };
    // DEV eligibility injection metadata
    eligibilitySource?: 'REAL' | 'DEV_INJECTED';
    devInjectedAgentIds?: string[];
    capturedAt: number;
    source: 'lab' | 'retry' | 'review';
  } | null;
  routed_to_agent_id: string | null;
  routed_to_agent_name: string | null;
  charged_amount: number | null;
  contract_id: string | null;
  validation_errors: Array<{
    field: string;
    message: string;
    code: string;
    severity: 'HARD' | 'SOFT';
  }> | null;
  created_at: number;
  last_updated_at: number;
}

// --- Transform Functions ---

function rowToLead(row: LeadRow): LifecycleLead {
  return {
    id: row.id,
    leadTypeId: row.lead_type_id,
    leadTypeName: row.lead_type_name,
    vertical: row.vertical as 'life_insurance' | 'mortgage',
    state: row.state,
    estimatedValue: Number(row.estimated_value),
    status: row.status as LeadStatus,
    retryCount: row.retry_count,
    maxRetries: row.max_retries,
    nextRetryAt: row.next_retry_at ?? undefined,
    originalPrice: row.original_price ?? undefined,
    currentPrice: row.current_price ?? undefined,
    pricingSnapshot: row.pricing_snapshot ?? undefined,
    leadPayload: row.lead_payload ?? undefined,
    leadProfile: row.lead_profile ?? undefined,
    routingContext: row.routing_context ?? undefined,
    assignedAgentId: row.routed_to_agent_id ?? undefined,
    assignedAgentName: row.routed_to_agent_name ?? undefined,
    chargedAmount: row.charged_amount ?? undefined,
    contractId: row.contract_id ?? undefined,
    validationErrors: row.validation_errors ?? undefined,
    createdAt: row.created_at,
    lastUpdatedAt: row.last_updated_at,
  };
}

// --- Query Keys ---

export const leadsQueryKeys = {
  all: ['leads'] as const,
  byId: (id: string) => ['leads', id] as const,
  byStatus: (status: LeadStatus) => ['leads', 'status', status] as const,
  retryReady: () => ['leads', 'retry-ready'] as const,
  stats: () => ['leads', 'stats'] as const,
};

// --- Hooks ---

export function useLeads() {
  return useQuery({
    queryKey: leadsQueryKeys.all,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as LeadRow[]).map(rowToLead);
    },
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: leadsQueryKeys.byId(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data ? rowToLead(data as unknown as LeadRow) : null;
    },
    enabled: !!id,
  });
}

export function useLeadsByStatus(status: LeadStatus) {
  return useQuery({
    queryKey: leadsQueryKeys.byStatus(status),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as LeadRow[]).map(rowToLead);
    },
  });
}

export function useRetryReadyLeads() {
  return useQuery({
    queryKey: leadsQueryKeys.retryReady(),
    queryFn: async () => {
      const now = Date.now();
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('status', 'RETRY_QUEUED')
        .lte('next_retry_at', now)
        .order('next_retry_at', { ascending: true });
      
      if (error) throw error;
      return (data as unknown as LeadRow[]).map(rowToLead);
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

export function useLeadStats() {
  return useQuery({
    queryKey: leadsQueryKeys.stats(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('status');
      
      if (error) throw error;
      
      const byStatus: Record<LeadStatus, number> = {
        NEW: 0,
        ROUTING_IN_PROGRESS: 0,
        ROUTED: 0,
        UNSOLD: 0,
        RETRY_QUEUED: 0,
        STOCKED: 0,
        ARCHIVED: 0,
        REVIEW_REQUIRED: 0,
        CREDITED: 0,
      };
      
      for (const row of data as { status: string }[]) {
        byStatus[row.status as LeadStatus]++;
      }
      
      return {
        total: data.length,
        byStatus,
        retriesQueued: byStatus.RETRY_QUEUED,
        stockedCount: byStatus.STOCKED,
      };
    },
  });
}

// --- Mutations ---

export function useCreateLead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (lead: LifecycleLead) => {
      const { data, error } = await supabase
        .from('leads')
        .insert({
          id: lead.id,
          lead_type_id: lead.leadTypeId,
          lead_type_name: lead.leadTypeName,
          vertical: lead.vertical,
          state: lead.state,
          estimated_value: lead.estimatedValue,
          status: lead.status as "NEW" | "ROUTING_IN_PROGRESS" | "ROUTED" | "UNSOLD" | "RETRY_QUEUED" | "STOCKED" | "ARCHIVED" | "REVIEW_REQUIRED" | "CREDITED",
          retry_count: lead.retryCount,
          max_retries: lead.maxRetries,
          next_retry_at: lead.nextRetryAt ?? null,
          original_price: lead.originalPrice ?? null,
          current_price: lead.currentPrice ?? null,
          pricing_snapshot: (lead.pricingSnapshot ?? null) as any,
          lead_payload: (lead.leadPayload ?? null) as any,
          lead_profile: (lead.leadProfile ?? null) as any,
          routing_context: (lead.routingContext ?? null) as any,
          routed_to_agent_id: lead.assignedAgentId ?? null,
          routed_to_agent_name: lead.assignedAgentName ?? null,
          charged_amount: lead.chargedAmount ?? null,
          contract_id: lead.contractId ?? null,
          created_at: lead.createdAt,
          last_updated_at: lead.lastUpdatedAt,
        } as any)
        .select()
        .single();
      
      if (error) throw error;
      return rowToLead(data as unknown as LeadRow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (lead: LifecycleLead) => {
      const { data, error } = await supabase
        .from('leads')
        .update({
          lead_type_id: lead.leadTypeId,
          lead_type_name: lead.leadTypeName,
          vertical: lead.vertical,
          state: lead.state,
          estimated_value: lead.estimatedValue,
          status: lead.status as "NEW" | "ROUTING_IN_PROGRESS" | "ROUTED" | "UNSOLD" | "RETRY_QUEUED" | "STOCKED" | "ARCHIVED" | "REVIEW_REQUIRED" | "CREDITED",
          retry_count: lead.retryCount,
          max_retries: lead.maxRetries,
          next_retry_at: lead.nextRetryAt ?? null,
          original_price: lead.originalPrice ?? null,
          current_price: lead.currentPrice ?? null,
          pricing_snapshot: (lead.pricingSnapshot ?? null) as any,
          lead_payload: (lead.leadPayload ?? null) as any,
          lead_profile: (lead.leadProfile ?? null) as any,
          routing_context: (lead.routingContext ?? null) as any,
          routed_to_agent_id: lead.assignedAgentId ?? null,
          routed_to_agent_name: lead.assignedAgentName ?? null,
          charged_amount: lead.chargedAmount ?? null,
          contract_id: lead.contractId ?? null,
          last_updated_at: lead.lastUpdatedAt,
        } as any)
        .eq('id', lead.id)
        .select()
        .single();
      
      if (error) throw error;
      return rowToLead(data as unknown as LeadRow);
    },
    onSuccess: (lead) => {
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.byId(lead.id) });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
    },
  });
}

export function useDeleteAllLeads() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('leads')
        .delete()
        .neq('id', ''); // Delete all
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
    },
  });
}

// --- Review Actions ---

/**
 * Approve a REVIEW_REQUIRED lead and transition to NEW for routing.
 * Clears validation_errors and updates leadTypeId/state.
 */
export function useApproveReviewLead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: {
      leadId: string;
      leadTypeId: string;
      leadTypeName: string;
      vertical: 'life_insurance' | 'mortgage';
      state: string;
    }) => {
      const now = Date.now();
      const { data, error } = await supabase
        .from('leads')
        .update({
          lead_type_id: params.leadTypeId,
          lead_type_name: params.leadTypeName,
          vertical: params.vertical,
          state: params.state.toUpperCase(),
          status: 'NEW' as const,
          validation_errors: null,
          last_updated_at: now,
        })
        .eq('id', params.leadId)
        .eq('status', 'REVIEW_REQUIRED') // Safety: only update if still REVIEW_REQUIRED
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
    },
  });
}

/**
 * Archive a REVIEW_REQUIRED lead.
 * Transitions to ARCHIVED status.
 */
export function useArchiveReviewLead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (leadId: string) => {
      const now = Date.now();
      const { data, error } = await supabase
        .from('leads')
        .update({
          status: 'ARCHIVED' as const,
          last_updated_at: now,
        })
        .eq('id', leadId)
        .eq('status', 'REVIEW_REQUIRED') // Safety: only update if still REVIEW_REQUIRED
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() });
    },
  });
}
